import { useState } from 'react';
import { Link } from 'react-router-dom';
import { dramas, categories, getFeaturedDramas } from '@/mocks/dramas';

function Hero() {
  return (
    <section className="relative w-full h-[520px] md:h-[650px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20movie%20theater%20environment%20with%20warm%20orange%20ambient%20lighting%2C%20velvet%20curtains%2C%20film%20projector%20beam%20cutting%20through%20darkness%2C%20atmospheric%20haze%2C%20dramatic%20contrast%2C%20luxurious%20and%20intimate%20home%20theater%20setting%2C%20cinematic%20mood&width=1600&height=900&seq=hero-bg-main&orientation=landscape"
          alt="桔子影视 - 高清电视剧在线观看"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center">
        <div className="animate-fade-in-up">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-white mb-5 leading-tight">
            桔子影视
          </h1>
          <p className="text-base md:text-xl text-white/80 mb-3 font-light">
            电视剧大全 · 最新全集 · 热播剧高清免费在线看
          </p>
          <p className="text-sm md:text-base text-white/60 mb-8 max-w-2xl mx-auto leading-relaxed">
            收录最新热播剧集高清全集资源，免费在线观看不缓冲，追剧首选平台
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/all"
              className="px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-base transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-play-circle-line mr-2"></i>
              立即追剧
            </Link>
            <Link
              to="/about"
              className="px-8 py-3.5 bg-white/15 hover:bg-white/25 text-white font-medium rounded-full text-base transition-all backdrop-blur-sm whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-4 md:gap-8 max-w-lg mx-auto animate-fade-in" style={{ animationDelay: '0.3s' }}>
          {[
            { num: '5000+', label: '电视剧资源' },
            { num: '1000万+', label: '活跃用户' },
            { num: '4K', label: '超清画质' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-xl md:text-3xl font-black text-primary-400">{stat.num}</div>
              <div className="text-xs md:text-sm text-white/60 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featured = getFeaturedDramas();

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              热播推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">精选好剧，不容错过</p>
          </div>
          <Link
            to="/category/all"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 stagger-children">
          {featured.map((drama) => (
            <Link
              key={drama.id}
              to={`/drama/${drama.id}`}
              className="group cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                <img
                  src={drama.coverImage}
                  alt={drama.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                  <span className="text-white text-xs font-medium bg-primary-500/90 px-2 py-1 rounded whitespace-nowrap">
                    {drama.rating} 分
                  </span>
                </div>
                {drama.status === '更新中' && (
                  <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap">
                    更新中
                  </span>
                )}
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                {drama.title}
              </h3>
              <p className="text-xs text-foreground-500 mt-1">{drama.updateInfo}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryShowcase() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
            分类浏览
          </h2>
          <p className="text-sm text-foreground-500 mt-2">按类型找到你喜欢的剧集</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4 stagger-children">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex flex-col items-center gap-3 p-5 rounded-xl bg-background-50 hover:bg-primary-50 transition-all hover:scale-105 cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-primary-100 rounded-xl group-hover:bg-primary-200 transition-colors">
                <i className={`${cat.icon} text-xl md:text-2xl text-primary-600`}></i>
              </span>
              <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              最新更新
            </h2>
            <p className="text-sm text-foreground-500 mt-1">实时更新，追剧不等待</p>
          </div>
          <Link
            to="/category/all"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
          >
            更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 stagger-children">
          {dramas.slice(0, 12).map((drama) => (
            <Link
              key={drama.id}
              to={`/drama/${drama.id}`}
              className="group cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                <img
                  src={drama.coverImage}
                  alt={drama.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-2 left-2 right-2">
                  <span className="text-white text-xs bg-black/60 px-2 py-1 rounded whitespace-nowrap">
                    {drama.episodes}集 · {drama.year}
                  </span>
                </div>
                {drama.status === '更新中' && (
                  <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap">
                    更新中
                  </span>
                )}
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                {drama.title}
              </h3>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-xs text-secondary-600 bg-secondary-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                  {categories.find((c) => c.slug === drama.categories[0])?.name}
                </span>
                <span className="text-xs text-foreground-400">{drama.rating}分</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      q: '桔子影视是否真的免费？',
      a: '是的，桔子影视所有电视剧资源均免费提供在线观看，无需付费会员，无隐藏消费。我们致力于为广大追剧爱好者提供优质的免费观剧体验。',
    },
    {
      q: '支持哪些设备观看？',
      a: '桔子影视支持电脑、平板、手机等多种设备在线观看。只需使用浏览器访问我们的网站，即可随时随地观看您喜爱的电视剧。',
    },
    {
      q: '电视剧更新速度如何？',
      a: '桔子影视与各大平台保持同步更新，热播剧集上线后我们会在第一时间更新资源，确保您不错过任何精彩剧情。',
    },
    {
      q: '如何找到想看的电视剧？',
      a: '您可以通过首页的分类导航按类型筛选剧集，也可以使用搜索功能直接搜索电视剧名称。我们提供了古装、现代、悬疑、爱情、喜剧等多种分类。',
    },
    {
      q: '画质如何？有广告吗？',
      a: '桔子影视提供高清甚至4K超清画质选择，让您享受极致观影体验。我们严格控制广告数量，确保流畅的观剧体验不被打断。',
    },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500 mt-2">关于桔子影视，你想知道的都在这里</p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="rounded-xl bg-background-50 overflow-hidden transition-all"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
              >
                <span className="text-sm md:text-base font-semibold text-foreground-800 pr-4">
                  {faq.q}
                </span>
                <span className={`w-6 h-6 flex items-center justify-center rounded-full transition-all flex-shrink-0 ${
                  openIndex === i ? 'bg-primary-500 text-background-50 rotate-45' : 'bg-background-100 text-foreground-500'
                }`}>
                  <i className="ri-add-line text-sm"></i>
                </span>
              </button>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  openIndex === i ? 'max-h-60 pb-4' : 'max-h-0'
                }`}
              >
                <p className="px-5 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <FeaturedSection />
      <CategoryShowcase />
      <LatestSection />
      <FAQSection />
    </main>
  );
}